'use client';

import { useTranslations } from '@power-core/toolkit';
import type { FunctionComponent } from 'react';

import { AbbTriangleDown } from '@/assets/icons/components/abb-triangle-down';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { DocumentList } from '@/modules/document/components/document-list/document-list';
import { useDocumentStatusesInit } from '@/modules/document-status/hooks/use-document-statuses-init';
import { useCheckTranslatedString } from '@/modules/utils/custom-hooks/use-check-translated-string';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { Button } from '@/ui-components/button/button';
import { SearchableDropdown } from '@/ui-components/dropdown/searchable-dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { Tooltip } from '@/ui-components/tooltip/tooltip';

import type { ProjectRowProps } from './types';

export const ProjectRow: FunctionComponent<ProjectRowProps> = ({ project, projectsOptions, isOpen, onToggle }) => {
	const t = useTranslations();
	// const { title, documentId: projectId, country, customer } = project;
	const { documentStatuses } = useDocumentStatusesInit(project?.documentId || '', isOpen);
	const { setSearchStatusId, customers } = useAppContext();
	const { addSearchParam } = useNavigation();
	const { isDarkTheme } = useTheme();
	const checkTranslatedString = useCheckTranslatedString();
	const customerName = customers?.find((c) => c.documentId === project?.customer?.documentId)?.name;
	const statuses =
		documentStatuses?.map((status) => {
			const { documentId, name, isDefault } = status;
			const shouldTranslate = isDefault || name?.includes('T_STATUS');

			return {
				value: documentId,
				label: shouldTranslate ? checkTranslatedString(name) : (name ?? ''),
			};
		}) ?? [];

	const statusAddButton = (
		<Button
			variant='secondary'
			sizing='small'
			className='mx-auto mb-3 w-[90%]'
			onClick={() =>
				addSearchParam([
					{ key: 'modal', value: 'DocumentStatus' },
					{ key: 'id', value: project?.documentId || '' },
				])
			}
		>
			{t('T_MANAGE_STATUS')}
		</Button>
	);

	return (
		<Flexbox
			direction='col'
			className={`pt-3 ${isOpen ? 'shadow-sm-light shadow-n400_input_stroker bg-n50_hover m-0.5 rounded-card dark:shadow-n500_light_text dark:bg-bg_dark' : ''}`}
		>
			<Flexbox direction='col'>
				<Flexbox justify='between' onClick={onToggle} className={`cursor-pointer px-3 ${customerName ? 'pb-0' : 'pb-3'}`}>
					<Paragraph variant={isOpen ? 'text-base-semibold' : 'text-base'} className='w-5/6'>
						{project?.title}
					</Paragraph>

					<AbbTriangleDown
						size={20}
						fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'}
						className={`transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
					/>
				</Flexbox>
				{customerName && (
					<Paragraph variant={isOpen ? 'text-sm' : 'text-xs'} className='px-3 pb-1 text-n500_light_text'>
						{customerName}
					</Paragraph>
				)}
				{isOpen && (
					<Flexbox justify='start' items='center' className='gap-4 px-2'>
						<SearchableDropdown
							className='w-1/2'
							sizing='small'
							placeholder={t('T_SORT_BY_STATUS')}
							options={statuses}
							inputBackground='light'
							onSelect={(value) => !Array.isArray(value) && setSearchStatusId(statuses.filter((status) => status.value === value)[0].value)}
							onClear={() => setSearchStatusId('')}
							bottomButton={statusAddButton}
						/>
						<Tooltip content={t('T_EDIT_PROJECT')}>
							<Button
								variant='text'
								className='text-accent uppercase'
								onClick={() =>
									addSearchParam([
										{ key: 'modal', value: 'ProjectModal' },
										{ key: 'id', value: project?.documentId ?? '' },
										{ key: 'title', value: project?.title ?? '' },
										{ key: 'country', value: project?.country ?? '' },
										{ key: 'customerId', value: project?.customer?.documentId ?? '' },
									])
								}
							>
								{t('T_EDIT')}
							</Button>
						</Tooltip>
						<Tooltip content={t('T_DELETE_PROJECT')}>
							<Button
								variant='text'
								className='text-brand uppercase'
								onClick={() => {
									addSearchParam([
										{ key: 'modal', value: 'Alert' },
										{ key: 'id', value: project?.documentId ?? '' },
										{ key: 'type', value: 'project' },
									]);
								}}
							>
								{t('T_DELETE')}
							</Button>
						</Tooltip>
					</Flexbox>
				)}
			</Flexbox>
			<DocumentList documentStatuses={documentStatuses} isOpen={isOpen} projectId={project?.documentId} projectsOptions={projectsOptions} />
		</Flexbox>
	);
};
